import { useState, useEffect } from 'react';
import FooterDrawer from '@/components/ui/atoms/FooterDrawer';
import { IconCheck } from '@tabler/icons-react';
import { MButton } from '@/components/ui/atoms/MButtons';
import styles from './MDropDownDrawer.module.css';

export interface DropdownItem {
  value: string;
  label: string | React.ReactNode;
  icon?: React.ReactNode;
}

interface MDropDownDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  items: DropdownItem[];
  initialValue?: string; // for single-select
  onSelect: (value: string | string[]) => void;
  variant?: 'single' | 'multi';
  selectedValues?: string[]; // for multi-select
  onDone?: (selected: string[]) => void | Promise<void>;
  doneText?: string;
  doneLoading?: boolean;
  onOtherOptionClick?: (selected: string[]) => void | Promise<void>;
  height?: string;
  textVariant?: 'md';
}

/**
 * MDropDownDrawer component
 * A drawer that displays a list of selectable items with translations
 * Shows a checkmark next to the selected item
 */
const MDropDownDrawer: React.FC<MDropDownDrawerProps> = ({
  open,
  onClose,
  title,
  items,
  initialValue = '',
  onSelect,
  variant = 'single',
  selectedValues = [],
  onDone,
  doneText,
  doneLoading,
  onOtherOptionClick,
  height,
  textVariant
}) => {
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [multiSelected, setMultiSelected] = useState<string[]>(selectedValues);

  // Sync internal state with props
  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);
  useEffect(() => {
    // Only update if arrays are different
    if (
      selectedValues.length !== multiSelected.length ||
      !selectedValues.every((v, i) => v === multiSelected[i])
    ) {
      setMultiSelected(selectedValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    if(typeof items[0] === 'string') {
      throw new Error("First item should not be string");
    }
  }, [items]);

  const handleItemSelect = (value: string) => {
    setSelectedValue(value);
    onSelect(value);
    onClose(); // Close drawer after selection
  };

  const handleMultiToggle = (item: string) => {
    let next: string[];
    if (multiSelected.includes(item)) {
      next = multiSelected.filter(v => v !== item);
    } else {
      next = [...multiSelected, item];
    }
    setMultiSelected(next);
    // Do NOT call onSelect here; defer until Done/onClose
  };

  // Prevent keyboard from opening on mobile devices when drawer opens
  useEffect(() => {
    if (open) {
      // Add a class to the body to prevent keyboard from showing
      document.body.classList.add('no-keyboard');
      
      // Blur any active element to prevent keyboard
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    } else {
      document.body.classList.remove('no-keyboard');
    }
    
    return () => {
      document.body.classList.remove('no-keyboard');
    };
  }, [open]);

  return (
    <FooterDrawer
      open={open}
      onClose={() => {
        if (variant === 'multi') onSelect(multiSelected);
        onClose();
      }}
      title={title}
      showClose={variant !== 'multi'}
      height={height || 'auto'}
    >
      {variant === 'multi' && (
        <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
          <MButton
            variant="link"
            size="md"
            text={typeof doneText === 'string' ? doneText : 'Done'}
            state="default"
            style={{ padding: 0, background: 'none', fontWeight: 600, fontSize: 'var(--typography-font-size-body-base)' }}
            onClick={async () => {
              onSelect(multiSelected); // Always update parent state before onDone
              if (typeof onDone === 'function') {
                await onDone(multiSelected);
              } else {
                onClose();
              }
            }}
            className="clear"
            disabled={!!doneLoading}
          />
        </div>
      )}
      <div 
        className={variant === 'multi' ? `${styles.dropdownContainer} ${styles.multiDropdownContainer}` : styles.dropdownContainer}
        onTouchStart={(e) => {
          // Prevent keyboard from showing on mobile
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }}
      >
        {items.map(item => (
          variant === 'multi' ? (
            <div
              key={item.value}
              className={`${styles.dropdownItem} ${styles.multiDropdownItem} ${multiSelected.includes(item.value) ? styles.selected : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (item.value.trim().toLowerCase() === 'other' && typeof onOtherOptionClick === 'function') {
                  onOtherOptionClick(multiSelected);
                } else {
                  handleMultiToggle(item.value);
                }
              }}
              role="button"
              tabIndex={0}
              {...(multiSelected.includes(item.value) ? { 'aria-pressed': 'true' } : { 'aria-pressed': 'false' })}
            >
              <div className={`${styles.itemContent} ${item.icon ? styles.withIcon : ''}`}>
                {item.icon && <span className={styles.iconWrapper}>{item.icon}</span>}
                <span className={
                  textVariant === 'md'
                    ? `${styles.itemText} ${styles.itemTextMd}`
                    : styles.itemText
                } style={{ textAlign: 'left' }}>
                  {item.label}
                </span>
                {multiSelected.includes(item.value) && (
                  <IconCheck size={24} stroke={1.5} className={styles.checkIcon} />
                )}
              </div>
            </div>
          ) : (
            <div
              key={item.value}
              className={`${styles.dropdownItem} ${
                selectedValue === item.value ? styles.selected : ''
              }`}
              onClick={() => handleItemSelect(item.value)}
              role="button"
              tabIndex={0}
              {...(selectedValue === item.value ? { 'aria-pressed': 'true' } : { 'aria-pressed': 'false' })}
            >
              <div className={`${styles.itemContent} ${item.icon ? styles.withIcon : ''}`}>
                {item.icon && <span className={styles.iconWrapper}>{item.icon}</span>}
                <span className={
                  textVariant === 'md'
                    ? `${styles.itemText} ${styles.itemTextMd}`
                    : styles.itemText
                } style={{ textAlign: 'left' }}>
                  {item.label}
                </span>
                {selectedValue === item.value && (
                  <IconCheck size={24} stroke={1.5} className={styles.checkIcon} />
                )}
              </div>
            </div>
          )
        ))}
      </div>
    </FooterDrawer>
  );
};

export default MDropDownDrawer;
